import React from "react"
import { Select } from "antd"

import DataQButton from "shared/ui/Buttons/DataQButton.component"

import { STATISTIC_TYPE } from "types/ReportingDashboard/elements.types"
import { OperatorOption, SelectOption } from "types/ui.types"

const STATISTIC_TYPE_OPTIONS: OperatorOption[] = [
  { value: STATISTIC_TYPE.N, label: "Count (n)" },
  { value: STATISTIC_TYPE.BIG_N, label: "Weighted Count (N)" },
  { value: STATISTIC_TYPE.COL_PERCENT, label: "Percentage" },
]

/**
 * Props for PieChartVariableSelector component
 */
export interface PieChartVariableSelectorProps {
  /** Available select options from codebook */
  selectOptions: SelectOption[]
  /** Currently selected variable */
  selectedVariable: string | null
  /** Currently selected statistic type */
  selectedStatisticType: STATISTIC_TYPE
  /** Whether query instance is available */
  queryInstance: boolean
  /** Loading state */
  isLoading: boolean
  /** Queued for execution state */
  isQueuedForExecution: boolean
  /** Variable selection handler */
  onVariableSelect: (value: string) => void
  /** Statistic type change handler */
  onStatisticTypeChange: (value: string) => void
  /** Generate/run handler */
  onGenerate: () => void
}

/**
 * PieChartVariableSelector Component
 *
 * Handles variable selection interface for pie chart elements.
 * Provides variable dropdown, statistic type selection, and generate button.
 *
 * @param props - Component props
 * @returns JSX element for variable selection UI
 */
const PieChartVariableSelector: React.FC<PieChartVariableSelectorProps> = ({
  selectOptions,
  selectedVariable,
  selectedStatisticType,
  queryInstance,
  isLoading,
  isQueuedForExecution,
  onVariableSelect,
  onStatisticTypeChange,
  onGenerate,
}) => {
  return (
    <div className="p-2 bg-gray-50 bg-opacity-80 rounded-md shadow-sm">
      <div className="flex flex-col space-y-1.5">
        <div>
          <label className="text-xs font-medium mb-0.5 block">Variable</label>
          <Select
            showSearch
            placeholder="Select variable"
            optionFilterProp="label"
            className="w-full text-xs"
            options={selectOptions}
            value={selectedVariable}
            onChange={onVariableSelect}
            filterOption={(input, option) =>
              option?.label?.toLowerCase().includes(input.toLowerCase()) ??
              false
            }
            size="small"
            style={{ fontSize: "11px" }}
            dropdownStyle={{ fontSize: "11px" }}
          />
        </div>
        <div className="flex items-end space-x-1.5 pt-1">
          <div className="flex-grow">
            <label className="text-xs font-medium mb-0.5 block">
              Statistic Type
            </label>
            <Select
              placeholder="Select statistic type"
              className="w-full text-xs"
              options={STATISTIC_TYPE_OPTIONS}
              value={selectedStatisticType}
              onChange={onStatisticTypeChange}
              size="small"
              style={{ fontSize: "11px" }}
              dropdownStyle={{ fontSize: "11px" }}
            />
          </div>
          <div className="flex-shrink-0">
            <DataQButton
              isDisabled={
                !queryInstance ||
                !selectedVariable ||
                isLoading ||
                isQueuedForExecution
              }
              isLoading={isLoading || isQueuedForExecution}
              onClick={onGenerate}
              type="primary"
              size="small"
            >
              {isQueuedForExecution ? "Queued..." : "Run"}
            </DataQButton>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PieChartVariableSelector
